/**
 * t255u: who reacted to an item, and with what. A sideways row of chips ("All N", then each emoji with its count) over
 * the people (avatar, name, the emoji they used); a person opens their profile. On the phone it is the "Reactions"
 * bottom sheet (from the summary next to the pills, or a long-press on a pill); on a computer the same list in a popover.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { orderReactors, type Reactor } from '@tradeoutlet/shared';
import { useAuth } from '@/lib/auth';
import { useMembers } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Avatar } from '@/components/ui';
import { BottomSheet } from '@/mobile/BottomSheet';
import { useReactors, type PillData } from './useReactors';
import type { ReactorKind } from './batcher';

export const ALL = 'all';
export interface ReactorRow { user_id: string; display_name: string; emojis: string[] }

/** The rows for a tab: one per person, with every emoji they used on "All". The viewer comes first. */
export function reactorRows(pills: PillData[], byEmoji: Record<string, Reactor[]> | undefined, tab: string, me: string | null | undefined): ReactorRow[] {
  const shown = pills.filter(p => p.count > 0 && (tab === ALL || p.emoji === tab));
  const rows = new Map<string, ReactorRow>();
  for (const p of shown) {
    const people: Reactor[] = byEmoji?.[p.emoji] ?? p.user_ids.map(u => ({ user_id: u, display_name: 'Someone' }));
    for (const r of people) {
      const row = rows.get(r.user_id) ?? { user_id: r.user_id, display_name: r.display_name, emojis: [] };
      if (!row.emojis.includes(p.emoji)) row.emojis.push(p.emoji);
      rows.set(r.user_id, row);
    }
  }
  const order = orderReactors([...rows.values()].map(r => ({ user_id: r.user_id, display_name: r.display_name })), me);
  return order.map(r => rows.get(r.user_id)!);
}
export const reactionTotal = (pills: PillData[]) => pills.reduce((n, p) => n + Math.max(0, p.count), 0);

function ReactorsView({ kind, id, pills, initial, onPerson }: { kind: ReactorKind; id: string; pills: PillData[]; initial: string; onPerson: (userId: string) => void }) {
  const { userId } = useAuth();
  const { byId } = useMembers();
  const q = useReactors(kind, id, pills);
  const withPeople = pills.filter(p => p.count > 0);
  const [tab, setTab] = useState(initial === ALL || withPeople.some(p => p.emoji === initial) ? initial : ALL);
  const rows = reactorRows(withPeople, q.data, tab, userId);
  const tabs = [{ key: ALL, label: <>All <span className="tabular-nums">{reactionTotal(withPeople)}</span></> as ReactNode }, ...withPeople.map(p => ({ key: p.emoji, label: <>{p.emoji} <span className="tabular-nums">{p.count}</span></> as ReactNode }))];
  const strip = useRef<HTMLDivElement>(null);
  return (
    <div data-testid="reactors-view">
      <div ref={strip} role="tablist" aria-label="Reactions" className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2" data-testid="reactors-tabs"
        onKeyDown={e => {
          const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          const i = tabs.findIndex(t => t.key === tab);
          setTab(tabs[(i + d + tabs.length) % tabs.length]!.key);
          setTimeout(() => strip.current?.querySelector<HTMLElement>('[role=tab][aria-selected=true]')?.focus(), 0);
        }}>
        {tabs.map(t => (
          <button key={t.key} type="button" role="tab" aria-selected={t.key === tab} tabIndex={t.key === tab ? 0 : -1} onClick={() => setTab(t.key)} data-testid="reactors-tab" data-tab={t.key}
            className={clsx('h-9 shrink-0 rounded-full border px-3 text-sm font-medium', t.key === tab ? 'border-ink bg-ink text-white dark:border-white dark:bg-white dark:text-ink' : 'border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-200')}>
            {t.label}
          </button>
        ))}
      </div>
      <ul role="tabpanel" aria-label={tab === ALL ? 'All reactions' : `Reacted with ${tab}`} className="-mx-1" data-testid="reactors-list">
        {q.isLoading && !rows.length && <li className="px-1 py-2 text-sm text-slate-500">Loading…</li>}
        {rows.map(r => (
          <li key={r.user_id}>
            <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); onPerson(r.user_id); }} aria-label={`${r.user_id === userId ? 'You' : r.display_name} ${r.emojis.join(' ')}`} data-testid="reactor-person"
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-1 text-left active:bg-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 dark:active:bg-slate-800">
              <Avatar size={32} name={r.display_name} id={r.user_id} url={byId.get(r.user_id)?.avatar_url} />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 dark:text-white">{r.user_id === userId ? 'You' : r.display_name}</span>
              <span className="shrink-0 text-lg" aria-hidden data-testid="reactor-emoji">{r.emojis.join(' ')}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Keeps the sheet's taps and long-presses out of the card around it (React events bubble through portals). */
const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
const fence = { onClick: stop, onTouchStart: stop, onTouchEnd: stop, onTouchMove: stop, onContextMenu: stop, onPointerDown: stop, onKeyDown: stop };

export function ReactionsSheet({ kind, id, pills, initial = ALL, onClose }: { kind: ReactorKind; id: string; pills: PillData[]; initial?: string; onClose: () => void }) {
  const openPerson = useUi(s => s.openPerson);
  return (
    <span className="contents" {...fence}>
      <BottomSheet open onClose={onClose} title="Reactions" testId="reactions-sheet">
        <div className="px-4 pb-3"><ReactorsView kind={kind} id={id} pills={pills} initial={initial} onPerson={u => { onClose(); openPerson(u); }} /></div>
      </BottomSheet>
    </span>
  );
}

/** The box a popover must stay inside: the nearest scrolling or clipping ancestor (a chat list, a thread panel), else the window. */
export function clipBox(el: HTMLElement): { top: number; bottom: number; left: number; right: number } {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const o = getComputedStyle(p);
    if (/(auto|scroll|hidden|clip)/.test(`${o.overflowY}${o.overflowX}`)) { const r = p.getBoundingClientRect(); return { top: r.top, bottom: r.bottom, left: r.left, right: r.right }; }
  }
  return { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
}

/**
 * t295u follow-up: where the popover goes. Below its reactions when it fits; above when it doesn't and there is more room
 * there; otherwise whichever side has more room, with the list capped to that space. Left-aligned unless that would run
 * past the right edge.
 */
export function placePopover(anchor: { top: number; bottom: number; left: number }, size: { width: number; height: number }, box: { top: number; bottom: number; left: number; right: number }, gap = 8) {
  const below = box.bottom - anchor.bottom - gap;
  const above = anchor.top - box.top - gap;
  const up = size.height > below && above > below;
  const room = Math.max(120, up ? above : below);
  const alignRight = anchor.left + size.width > box.right && anchor.left - box.left > size.width / 2;
  return { up, alignRight, maxHeight: Math.floor(room) };
}

export function ReactionsPopover({ kind, id, pills, initial = ALL, onClose }: { kind: ReactorKind; id: string; pills: PillData[]; initial?: string; onClose: () => void }) {
  const openPerson = useUi(s => s.openPerson);
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ up: boolean; alignRight: boolean; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current; const anchor = el?.parentElement;
    if (!el || !anchor) return;
    const a = anchor.getBoundingClientRect();
    setPlace(placePopover(a, { width: el.offsetWidth, height: el.scrollHeight }, clipBox(anchor)));
  }, []);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('[role=tab][aria-selected=true]')?.focus();
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    window.addEventListener('mousedown', down);
    return () => { window.removeEventListener('mousedown', down); opener?.focus?.(); };
  }, [onClose]);
  return (
    <div ref={ref} role="dialog" aria-label="Reactions" data-testid="reactions-popover" {...fence}
      onKeyDown={e => { e.stopPropagation(); if (e.key === 'Escape') onClose(); }}
      data-placement={place?.up ? 'top' : 'bottom'} style={{ visibility: place ? undefined : 'hidden' }}
      className={clsx('on-page absolute z-50 w-72 rounded-lg bg-white px-4 py-2 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700',
        place?.up ? 'bottom-full mb-2' : 'top-full mt-2', place?.alignRight ? 'right-0' : 'left-0')}>
      <div className="scroll-thin max-h-72 overflow-y-auto" style={place ? { maxHeight: Math.min(288, place.maxHeight - 16) } : undefined}><ReactorsView kind={kind} id={id} pills={pills} initial={initial} onPerson={u => { onClose(); openPerson(u); }} /></div>
    </div>
  );
}

/** t295u: how many different people reacted (someone using two emoji counts once). */
export const reactorCount = (pills: PillData[]) => new Set(pills.filter(p => p.count > 0).flatMap(p => p.user_ids)).size || reactionTotal(pills);
